/**
 * Serverzustand der Beitraege und Anhaenge.
 *
 * Ein neuer Beitrag beruehrt das Ticket -- der Server setzt `updated_at`
 * nach. Deshalb wird nach jedem Schreibvorgang auch die Ticketabfrage
 * ungueltig, sonst stuende in der Kopfzeile ein Aenderungsdatum, das nicht
 * mehr stimmt.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../lib/apiError';
import { ticketKeys } from '../queries';
import {
  createArticle,
  deleteArticle,
  deleteAttachment,
  listArticles,
  listTicketAttachments,
  getReplyDefaults,
  publishArticle,
  updateArticle,
  uploadAttachment,
  type ArticleCreate,
  type ArticleUpdate,
} from './api';

export const articleKeys = {
  list: (ticketId: string) => ['tickets', 'articles', ticketId] as const,
};

export const attachmentKeys = {
  list: (ticketId: string) => ['tickets', 'attachments', ticketId] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/**
 * Vorbelegung der Antwort -- F3-22.
 *
 * Erst geholt, wenn jemand "Antwort" waehlt: Wer eine Notiz schreibt,
 * braucht die Kontakte des Kunden nicht. Nicht zwischengespeichert ueber
 * den Reiter hinaus -- ist inzwischen eine Mail mit neuer CC eingegangen,
 * soll die naechste Antwort sie kennen.
 */
export function useReplyDefaults(ticketId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['tickets', 'reply-defaults', ticketId] as const,
    queryFn: () => getReplyDefaults(ticketId),
    enabled,
    staleTime: 0,
    retry: retryUnlessPermanent,
  });
}

export function useArticles(ticketId: string) {
  return useQuery({
    queryKey: articleKeys.list(ticketId),
    queryFn: () => listArticles(ticketId),
    retry: retryUnlessPermanent,
  });
}

export function useTicketAttachments(ticketId: string) {
  return useQuery({
    queryKey: attachmentKeys.list(ticketId),
    queryFn: () => listTicketAttachments(ticketId),
    retry: retryUnlessPermanent,
  });
}

function useInvalidateTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: articleKeys.list(ticketId) }),
      // Die Gesamtliste der Anhaenge haengt an denselben Vorgaengen: Ein
      // Beitrag ordnet Dateien zu, ein Loeschen nimmt sie heraus. Ohne diese
      // Zeile zeigte der Reiter einen Stand, den es nicht mehr gibt.
      queryClient.invalidateQueries({ queryKey: attachmentKeys.list(ticketId) }),
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) }),
    ]);
  };
}

export function useCreateArticle(ticketId: string) {
  const invalidate = useInvalidateTicket(ticketId);
  return useMutation({
    mutationFn: (payload: ArticleCreate) => createArticle(ticketId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateArticle(ticketId: string) {
  const invalidate = useInvalidateTicket(ticketId);
  return useMutation({
    mutationFn: ({ articleId, payload }: { articleId: string; payload: ArticleUpdate }) =>
      updateArticle(ticketId, articleId, payload),
    onSuccess: invalidate,
  });
}

export function usePublishArticle(ticketId: string) {
  const invalidate = useInvalidateTicket(ticketId);
  return useMutation({
    mutationFn: (articleId: string) => publishArticle(ticketId, articleId),
    onSuccess: invalidate,
  });
}

export function useDeleteArticle(ticketId: string) {
  const invalidate = useInvalidateTicket(ticketId);
  return useMutation({
    mutationFn: (articleId: string) => deleteArticle(ticketId, articleId),
    onSuccess: invalidate,
  });
}

export function useUploadAttachment(ticketId: string) {
  const invalidate = useInvalidateTicket(ticketId);
  return useMutation({
    mutationFn: (file: File) => uploadAttachment(ticketId, file),
    onSuccess: invalidate,
  });
}

export function useDeleteAttachment(ticketId: string) {
  const invalidate = useInvalidateTicket(ticketId);
  return useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(attachmentId),
    onSuccess: invalidate,
  });
}
